// Tonhöhe verschieben mit TD-PSOLA (pitch-synchrones Overlap-Add): Die Stimme wird an Periodenmarken in Körner
// zerlegt (je Korn zwei Perioden, Fensterhälften so lang wie die Nachbarperioden) und mit dem Abstand Periode / r
// wieder zusammengesetzt. Länge und Klangfarbe (Formanten) bleiben erhalten. Ohne Verschiebung ergibt sich exakt
// das Original (die Fensterhälften benachbarter Körner ergänzen sich zu 1). Stimmlose Stellen bleiben unverändert.
// Reine Funktion, läuft im Elastic-Worker.

/**
 * chs: Kanäle; f0At(i): Grundfrequenz (Hz) an Sample i, 0 = stimmlos; shiftAt(i): Verschiebung in Halbtönen an Sample i.
 */
export function psola(chs: Float32Array[], sr: number, f0At: (i: number) => number, shiftAt: (i: number) => number): Float32Array[] {
  const len = chs[0].length;
  if (len < 4) return chs.map((c) => c.slice());
  const raw = new Float32Array(len);
  for (const c of chs) for (let i = 0; i < len; i++) raw[i] += c[i];
  // Marken auf einem tiefpassgefilterten Signal (~900 Hz, vor- und rückwärts, ohne Phasenversatz) suchen
  const lp = new Float32Array(len), al = 1 - Math.exp((-2 * Math.PI * 900) / sr);
  let y1 = 0, y2 = 0; for (let i = 0; i < len; i++) { y1 += al * (raw[i] - y1); y2 += al * (y1 - y2); lp[i] = y2; }
  y1 = 0; y2 = 0; for (let i = len - 1; i >= 0; i--) { y1 += al * (lp[i] - y1); y2 += al * (y1 - y2); lp[i] = y2; }

  // 1) Analyse-Marken: stimmhaft eine je Periode auf dem größten Ausschlag (Suche Periode ±15 % nach der vorigen),
  //    stimmlos alle 5 ms
  const UV = Math.max(16, Math.round(0.005 * sr));
  const marks: number[] = [], voiced: boolean[] = [];
  let i = 0;
  while (i < len) {
    const f = f0At(i);
    if (f > 30) {
      const T = sr / f, last = marks.length ? marks[marks.length - 1] : -1, cont = last >= 0 && voiced[voiced.length - 1] && i - last < 1.5 * T;
      const lo = Math.max(cont ? last + 1 : 0, Math.round(cont ? last + 0.85 * T : i)), hi = Math.min(len - 1, Math.round(cont ? last + 1.15 * T : i + T));
      let m = lo, bv = -Infinity;
      for (let k = lo; k <= hi; k++) if (lp[k] > bv) { bv = lp[k]; m = k; }
      if (last >= 0 && m <= last) m = last + Math.max(1, Math.round(T));
      if (m >= len) break;
      marks.push(m); voiced.push(true);
      i = Math.round(m + T);
    } else {
      const m = marks.length ? Math.max(i, marks[marks.length - 1] + 1) : i;
      marks.push(m); voiced.push(false);
      i = m + UV;
    }
  }
  const n = marks.length;
  if (n < 3) return chs.map((c) => c.slice());
  const P = (k: number) => (k + 1 < n ? marks[k + 1] - marks[k] : marks[k] - marks[k - 1]);   // Periode rechts der Marke

  // 2) Synthese: Ausgabemarken im Abstand P / r; jede nimmt das Korn der zeitlich nächsten Analyse-Marke
  const out = chs.map(() => new Float32Array(len)), norm = new Float32Array(len);
  let ts = marks[0], k = 0;
  while (ts < len) {
    while (k + 1 < n && Math.abs(marks[k + 1] - ts) <= Math.abs(marks[k] - ts)) k++;
    const c = marks[k], Pl = k > 0 ? marks[k] - marks[k - 1] : P(k), Pr = P(k);
    // Stimmhaft nur verschieben, wenn beide Nachbarn auch stimmhaft sind (an Übergängen bleibt das Original)
    const v = voiced[k] && (k === 0 || voiced[k - 1]) && (k + 1 >= n || voiced[k + 1]);
    const r = v ? Math.pow(2, Math.max(-12, Math.min(12, shiftAt(c))) / 12) : 1;
    const x0 = Math.round(ts);
    for (let d = -Pl + 1; d < Pr; d++) {
      const si = c + d, di = x0 + d;
      if (si < 0 || si >= len || di < 0 || di >= len) continue;
      const w = d < 0 ? 0.5 + 0.5 * Math.cos((Math.PI * d) / Pl) : 0.5 + 0.5 * Math.cos((Math.PI * d) / Pr);
      for (let ch = 0; ch < chs.length; ch++) out[ch][di] += chs[ch][si] * w;
      norm[di] += w;
    }
    ts += Pr / r;
  }
  // Fensterüberlappung ausgleichen (ohne Verschiebung ist sie überall 1)
  for (let ch = 0; ch < chs.length; ch++) { const o = out[ch]; for (let q = 0; q < len; q++) { const w = norm[q]; if (w > 0.05) o[q] /= w; } }
  return out;
}
